import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';
import { OPERATIONS_TABS } from '../config/pages';

// ── Platform config ───────────────────────────────────────────────────────────
const SCH_PLATFORMS = [
  { id:'ig',  name:'Instagram',       color:'#e1306c', bg:'#fce4ec' },
  { id:'fb',  name:'Facebook',        color:'#1877f2', bg:'#e7f0fd' },
  { id:'wa',  name:'WhatsApp Status', color:'#25d366', bg:'#e6f7ec' },
  { id:'li',  name:'LinkedIn',        color:'#0a66c2', bg:'#e1ecf7' },
  { id:'yt',  name:'YouTube',         color:'#ff0000', bg:'#fde7e7' },
  { id:'x',   name:'X / Twitter',    color:'#000000', bg:'#e7e7e7' },
  { id:'pi',  name:'Pinterest',       color:'#bd081c', bg:'#fbe1e3' },
  { id:'th',  name:'Threads',         color:'#000000', bg:'#e7e7e7' },
  { id:'gg',  name:'Google Ads',      color:'#4285f4', bg:'#e8f0fe' },
  { id:'ma',  name:'Meta Ads',        color:'#0668e1', bg:'#e7f0fd' },
  { id:'tk',  name:'TikTok',          color:'#010101', bg:'#f0f0f0' },
  { id:'tg',  name:'Telegram',        color:'#229ed9', bg:'#e3f4fc' },
];

// Fallback metrics (used only if master-data API doesn't return platform_metrics)
const FALLBACK_METRICS = {
  'Instagram':       ['Reach','Likes','Comments','Shares','Saves','Profile Visits'],
  'Facebook':        ['Reach','Reactions','Comments','Shares','Link Clicks'],
  'LinkedIn':        ['Impressions','Reactions','Comments','Reposts','Clicks'],
  'WhatsApp Status': ['Views','Replies'],
  'X / Twitter':    ['Impressions','Likes','Reposts','Replies','Profile Visits'],
  'Google Ads':      ['Impressions','Clicks','CTR','Conversions','Cost'],
  'YouTube':         ['Views','Watch Time','Likes','Comments','Subscribers Gained'],
  'Pinterest':       ['Impressions','Saves','Clicks','Outbound Clicks'],
  'TikTok':          ['Views','Likes','Comments','Shares','Followers Gained'],
  'Telegram':        ['Views','Forwards','Reactions'],
};

const STATUS_COLOR = {
  'Monitoring':     '#3b7ef0',
  'Needs Attention':'#f7c34f',
  'Action Required':'#e84545',
  'Closed':         '#1db87a',
};
const STATUS_ICON = {
  'Monitoring':     '📡',
  'Needs Attention':'⚠️',
  'Action Required':'🔴',
  'Closed':         '✅',
};
const TAG_COLOR = {
  'Good':            '#1db87a',
  'Needs Attention': '#f7c34f',
  'Issue':           '#e84545',
  'Client Feedback': '#6c58f0',
  'Info':            '#3b7ef0',
};

const sel = (w='100%', extra={}) => ({
  width: w, padding:'4px 8px', fontSize:'0.75rem',
  border:'1px solid var(--border)', borderRadius:6,
  background:'var(--bg)', color:'var(--text)',
  ...extra,
});
const lbl = { fontSize:'0.62rem', fontWeight:700, color:'var(--text3)', display:'block', marginBottom:2 };

const normStr = s => (s||'').toLowerCase().replace(/[^a-z0-9]/g,'');
const findPl  = name => SCH_PLATFORMS.find(p => normStr(p.name)===normStr(name) || normStr(p.id)===normStr(name));

// ── Platform logo icon slugs for SimpleIcons CDN ─────────────────────────────
const PLATFORM_ICON = {
  ig: 'instagram',    fb: 'facebook',   wa: 'whatsapp',
  li: 'linkedin',     yt: 'youtube',    x:  'x',
  pi: 'pinterest',    th: 'threads',    gg: 'google',
  ma: 'meta',         tk: 'tiktok',     tg: 'telegram',
};

function PlatformLogo({ id, name, size = 16 }) {
  const key = (id || '').toLowerCase();
  const nm  = normStr(name || '');
  const slug = PLATFORM_ICON[key]
    || PLATFORM_ICON[Object.keys(PLATFORM_ICON).find(k => normStr(PLATFORM_ICON[k])===nm) || ''];
  const s = { width: size, height: size, display:'block', flexShrink:0 };
  if (slug) {
    return (
      <img
        src={`https://cdn.simpleicons.org/${slug}`}
        alt={name || id}
        style={{ ...s, objectFit:'contain' }}
        onError={e => { e.target.style.display='none'; }}
      />
    );
  }
  // Fallback: show initials
  const label = (name || id || '??').slice(0,2).toUpperCase();
  return <span style={{ fontSize:'0.6rem', fontWeight:800 }}>{label}</span>;
}

const fmtDate = str => {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-IN', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit',
  });
};
const fmtDateShort = str => {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
};
const stars = n => n ? '★'.repeat(n) + '☆'.repeat(5-n) : '';

// ── Metric fields component ───────────────────────────────────────────────────
// platformMetrics: map of platformName → string[] (from API or fallback)
function MetricFields({ platformName, values, onChange, platformMetrics }) {
  const fields = (platformName && (platformMetrics[platformName] || FALLBACK_METRICS[platformName]))
    || ['Reach','Likes','Comments','Shares'];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(100px,1fr))', gap:5 }}>
      {fields.map(label => (
        <div key={label}>
          <label style={{ fontSize:'0.6rem', color:'var(--text3)', display:'block', marginBottom:1 }}>
            {label}
          </label>
          <input
            type="text"
            value={values[label] || ''}
            onChange={e => onChange({ ...values, [label]: e.target.value })}
            placeholder="0"
            style={{ width:'100%', padding:'3px 6px', fontSize:'0.72rem',
              border:'1px solid var(--border)', borderRadius:5,
              background:'var(--bg)', color:'var(--text)', boxSizing:'border-box' }}
          />
        </div>
      ))}
    </div>
  );
}

// ── Observation form row ──────────────────────────────────────────────────────
function ObsRow({ idx, platforms, teamMembers, data, onChange, onRemove, platformMetrics }) {

  const inp = (field, val) => onChange({ ...data, [field]: val });

  return (
    <div style={{ background:'var(--bg)', border:'1px solid var(--border)',
      borderRadius:7, padding:'8px 10px', marginBottom:6, position:'relative' }}>

      {idx > 0 && (
        <button onClick={onRemove} style={{ position:'absolute', top:5, right:7,
          background:'none', border:'none', cursor:'pointer', color:'var(--text3)',
          fontSize:'0.85rem', lineHeight:1 }}
          onMouseOver={e=>e.target.style.color='#e84545'}
          onMouseOut={e=>e.target.style.color='var(--text3)'}>✕</button>
      )}

      {/* Row 1: Observer / Platform / Tag / Rating / Action — all inline */}
      <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr 1.2fr 1fr 1.2fr', gap:6, marginBottom:6 }}>
        <div>
          <label style={lbl}>👤 Observer</label>
          <select value={data.author||''} onChange={e=>inp('author',e.target.value)} style={sel()}>
            <option value="">— Select —</option>
            {teamMembers.map(m => <option key={m.id||m.name} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>📲 Platform</label>
          <select value={data.platform||''} onChange={e=>inp('platform',e.target.value)} style={sel()}>
            <option value="">Overall</option>
            {platforms.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>🏷️ Tag</label>
          <select value={data.tag||'Info'} onChange={e=>inp('tag',e.target.value)} style={sel()}>
            <option value="Info">ℹ️ Info</option>
            <option value="Good">✅ Good</option>
            <option value="Needs Attention">⚠️ Needs Attention</option>
            <option value="Issue">🔴 Issue</option>
            <option value="Client Feedback">💬 Client Feedback</option>
          </select>
        </div>
        <div>
          <label style={lbl}>⭐ Rating</label>
          <select value={data.rating||''} onChange={e=>inp('rating',e.target.value)} style={sel()}>
            <option value="">—</option>
            <option value="5">★★★★★</option>
            <option value="4">★★★★☆</option>
            <option value="3">★★★☆☆</option>
            <option value="2">★★☆☆☆</option>
            <option value="1">★☆☆☆☆</option>
          </select>
        </div>
        <div>
          <label style={lbl}>⚡ Action</label>
          <select value={data.action||'None'} onChange={e=>inp('action',e.target.value)} style={sel()}>
            <option value="None">None</option>
            <option value="Reply needed">Reply needed</option>
            <option value="Boost recommended">Boost recommended</option>
            <option value="Escalate to client">Escalate to client</option>
            <option value="Remove post">Remove post</option>
            <option value="Re-post">Re-post</option>
          </select>
        </div>
      </div>

      {/* Note */}
      <textarea value={data.note||''} onChange={e=>inp('note',e.target.value)}
        placeholder={`Observation ${idx+1} — what did you notice?`}
        rows={2}
        style={{ width:'100%', padding:'5px 8px', fontSize:'0.75rem',
          border:'1px solid var(--border)', borderRadius:6, background:'var(--bg2)',
          color:'var(--text)', resize:'vertical', boxSizing:'border-box', marginBottom:5 }} />

      {/* Metrics — always expanded */}
      <div>
        <div style={{ fontSize:'0.62rem', fontWeight:700, color:'var(--text3)', marginBottom:5 }}>
          📊 Metrics
        </div>
        <MetricFields
          platformName={data.platform||''}
          values={data.metrics||{}}
          onChange={v => inp('metrics',v)}
          platformMetrics={platformMetrics}
        />
      </div>
    </div>
  );
}

// ── Edit Observation Modal ─────────────────────────────────────────────────────
function EditObsModal({ log, platforms, teamMembers, onSave, onClose, platformMetrics }) {
  const [form, setForm] = useState({
    author:   log.author   || '',
    platform: log.platform || '',
    tag:      log.tag      || 'Info',
    note:     log.note     || '',
    rating:   log.rating ? String(log.rating) : '',
    action:   log.action   || 'None',
    metrics:  log.metrics  || {},
  });

  const save = () => {
    if (!form.author || !form.note.trim()) { alert('Observer and Note are required'); return; }
    onSave({ ...form, rating: form.rating ? parseInt(form.rating) : null });
  };

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9000,
      background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg2)',
        border:'1px solid var(--border)', borderRadius:12, width:'100%', maxWidth:540,
        maxHeight:'88vh', overflow:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.3)' }}>

        <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)',
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontWeight:800, fontSize:'0.88rem' }}>✏️ Edit Observation</div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            cursor:'pointer', fontSize:'1rem', color:'var(--text3)' }}>✕</button>
        </div>

        <div style={{ padding:'14px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:8 }}>
            {[
              { lbl:'👤 Observer', val:form.author, opts: teamMembers.map(m=>({v:m.name,l:m.name})), onChange:v=>setForm({...form,author:v}), placeholder:'— Select —' },
              { lbl:'📲 Platform', val:form.platform, opts:[{v:'',l:'Overall'},...platforms.map(p=>({v:p,l:p}))], onChange:v=>setForm({...form,platform:v,metrics:{}}), placeholder:null },
              { lbl:'🏷️ Tag', val:form.tag, opts:[{v:'Info',l:'ℹ️ Info'},{v:'Good',l:'✅ Good'},{v:'Needs Attention',l:'⚠️ Needs Attention'},{v:'Issue',l:'🔴 Issue'},{v:'Client Feedback',l:'💬 Client Feedback'}], onChange:v=>setForm({...form,tag:v}), placeholder:null },
            ].map(({lbl:l,val,opts,onChange,placeholder}) => (
              <div key={l}>
                <label style={lbl}>{l}</label>
                <select value={val} onChange={e=>onChange(e.target.value)} style={sel()}>
                  {placeholder && <option value="">{placeholder}</option>}
                  {opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div style={{ marginBottom:8 }}>
            <label style={lbl}>Note</label>
            <textarea value={form.note} onChange={e=>setForm({...form,note:e.target.value})}
              rows={3}
              style={{ width:'100%', padding:'6px 8px', fontSize:'0.78rem',
                border:'1px solid var(--border)', borderRadius:6, background:'var(--bg)',
                color:'var(--text)', resize:'vertical', boxSizing:'border-box' }} />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
            <div>
              <label style={lbl}>⭐ Rating</label>
              <select value={form.rating} onChange={e=>setForm({...form,rating:e.target.value})} style={sel()}>
                <option value="">— No rating —</option>
                <option value="5">★★★★★ Excellent</option>
                <option value="4">★★★★☆ Good</option>
                <option value="3">★★★☆☆ Average</option>
                <option value="2">★★☆☆☆ Below Average</option>
                <option value="1">★☆☆☆☆ Poor</option>
              </select>
            </div>
            <div>
              <label style={lbl}>⚡ Action Required</label>
              <select value={form.action} onChange={e=>setForm({...form,action:e.target.value})} style={sel()}>
                <option value="None">None</option>
                <option value="Reply needed">Reply needed</option>
                <option value="Boost recommended">Boost recommended</option>
                <option value="Escalate to client">Escalate to client</option>
                <option value="Remove post">Remove post</option>
                <option value="Re-post">Re-post</option>
              </select>
            </div>
          </div>

          <details open>
            <summary style={{ fontSize:'0.68rem', fontWeight:700, color:'var(--text3)',
              cursor:'pointer', userSelect:'none', marginBottom:6 }}>📊 Metrics</summary>
            <div style={{ marginTop:6 }}>
              <MetricFields
                platformName={form.platform}
                values={form.metrics}
                onChange={v=>setForm({...form,metrics:v})}
                platformMetrics={platformMetrics}
              />
            </div>
          </details>
        </div>

        <div style={{ padding:'10px 16px', borderTop:'1px solid var(--border)',
          display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button onClick={onClose}
            style={{ padding:'5px 14px', fontSize:'0.78rem', fontWeight:700,
              background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7,
              cursor:'pointer', color:'var(--text2)' }}>
            Cancel
          </button>
          <button onClick={save}
            style={{ padding:'5px 16px', fontSize:'0.78rem', fontWeight:700,
              background:'var(--accent)', border:'none', borderRadius:7,
              cursor:'pointer', color:'#fff' }}>
            💾 Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Log entry display ─────────────────────────────────────────────────────────
function LogEntry({ log, onEdit, onDelete }) {
  const tc = TAG_COLOR[log.tag] || '#aaa';
  return (
    <div style={{ padding:'8px 10px', background:'var(--bg3)',
      borderRadius:7, borderLeft:`3px solid ${tc}`, marginBottom:6 }}>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4, flexWrap:'wrap' }}>
        <span style={{ padding:'1px 7px', background:`${tc}18`, color:tc,
          borderRadius:9, fontSize:'0.66rem', fontWeight:700 }}>{log.tag||'Info'}</span>
        <span style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--text2)' }}>👤 {log.author||'—'}</span>
        {log.platform && (
          <span style={{ fontSize:'0.65rem', padding:'1px 6px',
            background:'var(--bg2)', border:'1px solid var(--border)',
            borderRadius:7, color:'var(--text3)' }}>{log.platform}</span>
        )}
        <span style={{ fontSize:'0.62rem', color:'var(--text3)' }}>{fmtDate(log.date)}</span>
        {log.rating && <span style={{ color:'#f7c34f', fontSize:'0.72rem' }}>{stars(log.rating)}</span>}
        {log.action && log.action !== 'None' && (
          <span style={{ padding:'1px 7px', background:'#e8454518', color:'#e84545',
            borderRadius:9, fontSize:'0.63rem', fontWeight:700 }}>⚡ {log.action}</span>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:5 }}>
          <button onClick={onEdit}
            style={{ padding:'2px 9px', fontSize:'0.65rem', fontWeight:600, background:'var(--bg2)',
              border:'1px solid var(--border)', borderRadius:5, color:'var(--text2)', cursor:'pointer' }}
            onMouseOver={e=>{e.currentTarget.style.background='var(--accent)';e.currentTarget.style.color='#fff';}}
            onMouseOut={e=>{e.currentTarget.style.background='var(--bg2)';e.currentTarget.style.color='var(--text2)';}}>
            ✏️ Edit
          </button>
          <button onClick={onDelete}
            style={{ padding:'2px 9px', fontSize:'0.65rem', fontWeight:600, background:'var(--bg2)',
              border:'1px solid var(--border)', borderRadius:5, color:'var(--text2)', cursor:'pointer' }}
            onMouseOver={e=>{e.currentTarget.style.background='#e84545';e.currentTarget.style.color='#fff';e.currentTarget.style.borderColor='#e84545';}}
            onMouseOut={e=>{e.currentTarget.style.background='var(--bg2)';e.currentTarget.style.color='var(--text2)';e.currentTarget.style.borderColor='var(--border)';}}>
            🗑 Delete
          </button>
        </div>
      </div>
      <div style={{ fontSize:'0.79rem', color:'var(--text)', lineHeight:1.45, marginBottom: log.metrics && Object.keys(log.metrics).length > 0 ? 5 : 0 }}>
        {log.note}
      </div>
      {log.metrics && Object.keys(log.metrics).length > 0 && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {Object.entries(log.metrics).map(([label,val]) => (
            <span key={label} style={{ fontSize:'0.68rem', color:'var(--text3)' }}>
              {label}: <b style={{ color:'var(--text)' }}>{val}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Platform tile (compact) ───────────────────────────────────────────────────
function PlatformTile({ pName, platRec }) {
  const pl       = findPl(pName) || { name:pName, color:'#aaa', bg:'#eee' };
  const state    = platRec?.state || '';
  const postUrl  = platRec?.live_url || platRec?.url || platRec?.liveUrl || '';
  const postedAt = platRec?.posted_at || platRec?.postedAt || '';
  const isPosted = state === 'posted';
  const isSched  = state === 'scheduled';
  const borderC  = isPosted ? '#1db87a' : isSched ? '#3b7ef0' : 'var(--border)';
  const stateLabel = isPosted ? '✅ Live' : isSched ? '🗓 Scheduled' : '⏳ Not posted';
  const stateColor = isPosted ? '#1db87a' : isSched ? '#3b7ef0' : 'var(--text3)';

  return (
    <div style={{ background:'var(--bg2)', border:`1.5px solid ${borderC}`,
      borderRadius:8, padding:'7px 10px', minWidth:140 }}>
      <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:5 }}>
        <span style={{ width:26, height:26, borderRadius:5, background:pl.bg, flexShrink:0,
          display:'inline-flex', alignItems:'center', justifyContent:'center',
          border:`1px solid ${pl.color}44`, overflow:'hidden' }}>
          <PlatformLogo id={pl.id || ''} name={pl.name || pName} size={18} />
        </span>
        <div>
          <div style={{ fontSize:'0.75rem', fontWeight:700, color:pl.color, lineHeight:1.1 }}>{pName}</div>
          <div style={{ fontSize:'0.6rem', fontWeight:700, color:stateColor }}>{stateLabel}</div>
        </div>
      </div>
      {postedAt && (
        <div style={{ fontSize:'0.6rem', color:'var(--text3)', marginBottom:5 }}>
          📅 {fmtDateShort(postedAt)}
        </div>
      )}
      {postUrl ? (
        <a href={postUrl} target="_blank" rel="noreferrer"
          style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4,
            padding:'4px 0', background: isPosted ? '#1db87a' : '#3b7ef0',
            color:'#fff', borderRadius:6, fontSize:'0.68rem', fontWeight:800, textDecoration:'none' }}>
          🔗 View Live Post
        </a>
      ) : (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
          padding:'4px 0', background:'var(--bg3)', color:'var(--text3)',
          borderRadius:6, fontSize:'0.68rem', fontWeight:600, border:'1px dashed var(--border2)' }}>
          No live link yet
        </div>
      )}
    </div>
  );
}

// ── Active monitoring card ────────────────────────────────────────────────────
function MonCard({ wo, platRecs, monRec, teamMembers, onUpdate, platformMetrics }) {
  const [obsRows, setObsRows] = useState([
    { author:'', platform:'', tag:'Info', note:'', rating:'', action:'None', metrics:{} }
  ]);
  const [saving,    setSaving]    = useState(false);
  const [editLog,   setEditLog]   = useState(null);
  const [lightbox,  setLightbox]  = useState(false);

  // ESC to close lightbox
  useEffect(() => {
    if (!lightbox) return;
    const h = e => { if (e.key === 'Escape') setLightbox(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightbox]);

  const status = monRec.status || 'Monitoring';
  const logs   = monRec.logs   || [];
  const sc     = STATUS_COLOR[status] || '#aaa';
  const si     = STATUS_ICON[status]  || '📡';

  const [ey,em,ed] = (wo.event_date||'').split('-');
  const evDate = ed ? `${ed}-${em}-${ey}` : wo.event_date;

  // Platform names from the actual platRecs (platforms assigned to this WO),
  // NOT hardcoded — derived from the per-WO detail API response
  const platformNames = Object.values(platRecs)
    .map(rec => rec.platform || rec.name || rec.platform_name)
    .filter(Boolean);

  const addRow = () => setObsRows(r => [...r, { author:'', platform:'', tag:'Info', note:'', rating:'', action:'None', metrics:{} }]);
  const rmRow  = i  => setObsRows(r => r.filter((_,j)=>j!==i));
  const updRow = (i, v) => setObsRows(r => r.map((row,j)=>j===i?v:row));

  const saveAll = async () => {
    const valid = obsRows.filter(r => r.author && r.note.trim());
    if (!valid.length) { alert('Each observation needs an Observer and a Note.'); return; }
    setSaving(true);
    try {
      const newLogs = valid.map(r => ({
        id:       'ml' + Date.now() + Math.random().toString(36).slice(2),
        author:   r.author,
        platform: r.platform,
        tag:      r.tag || 'Info',
        note:     r.note.trim(),
        rating:   r.rating ? parseInt(r.rating) : null,
        action:   r.action || 'None',
        date:     new Date().toISOString(),
        metrics:  r.metrics || {},
      }));

      const updatedLogs = [...logs, ...newLogs];
      let newStatus = status;
      if (status !== 'Closed') {
        const hasIssue = updatedLogs.some(l => l.tag==='Issue' || (l.action && l.action!=='None'));
        const hasNeeds = updatedLogs.some(l => l.tag==='Needs Attention');
        if (hasIssue) newStatus = 'Action Required';
        else if (hasNeeds && status==='Monitoring') newStatus = 'Needs Attention';
      }

      await api.post(`/monitoring/${wo.id}`, { logs: updatedLogs, status: newStatus });
      onUpdate();
      setObsRows([{ author:'', platform:'', tag:'Info', note:'', rating:'', action:'None', metrics:{} }]);
    } catch(e) { console.error(e); alert('Failed to save. Please try again.'); }
    finally { setSaving(false); }
  };

  const setStatus = async (newStatus) => {
    try {
      await api.post(`/monitoring/${wo.id}`, { logs, status: newStatus });
      onUpdate();
    } catch(e) { console.error(e); }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm('Delete this observation?')) return;
    const updated = logs.filter(l => l.id !== logId);
    try {
      await api.post(`/monitoring/${wo.id}`, { logs: updated, status });
      onUpdate();
    } catch(e) { console.error(e); }
  };

  const saveEdit = async (logId, data) => {
    const updated = logs.map(l => l.id===logId ? { ...l, ...data, editedAt:new Date().toISOString() } : l);
    let newStatus = status;
    if (status !== 'Closed') {
      const hasIssue = updated.some(l => l.tag==='Issue' || (l.action && l.action!=='None'));
      const hasNeeds = updated.some(l => l.tag==='Needs Attention');
      if (hasIssue) newStatus = 'Action Required';
      else if (hasNeeds) newStatus = 'Needs Attention';
      else if (newStatus==='Action Required' || newStatus==='Needs Attention') newStatus = 'Monitoring';
    }
    try {
      await api.post(`/monitoring/${wo.id}`, { logs: updated, status: newStatus });
      onUpdate();
      setEditLog(null);
    } catch(e) { console.error(e); }
  };

  const creative = wo.creative?.status === 'Approved' ? wo.creative : null;

  return (
    <>
      <div style={{ background:'var(--bg2)', border:'1px solid var(--border)',
        borderLeft:`4px solid ${sc}`, borderRadius:10, marginBottom:12, overflow:'hidden' }}>

        {/* Card header */}
        <div style={{ padding:'8px 14px', display:'flex', alignItems:'center',
          gap:8, flexWrap:'wrap', borderBottom:'1px solid var(--border)' }}>
          <span style={{ fontFamily:'Space Mono,monospace', fontSize:'0.78rem',
            fontWeight:800, color:'var(--accent)' }}>{wo.wo_number||`WO-${wo.id}`}</span>
          {wo.client?.name && (
            <span style={{ padding:'2px 7px', background:'var(--bg3)',
              borderRadius:9, fontSize:'0.68rem', fontWeight:600 }}>{wo.client.name}</span>
          )}
          <span style={{ fontWeight:700, fontSize:'0.85rem' }}>{wo.title}</span>
          {wo.event_date && (
            <span style={{ fontSize:'0.68rem', color:'var(--text3)' }}>📅 {evDate}</span>
          )}
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
            <select value={status} onChange={e=>setStatus(e.target.value)}
              style={{ padding:'3px 8px', fontSize:'0.7rem', fontWeight:700,
                border:`1.5px solid ${sc}`, borderRadius:7,
                background:`${sc}11`, color:sc, cursor:'pointer' }}>
              {['Monitoring','Needs Attention','Action Required','Closed'].map(s=>(
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span style={{ fontSize:'1rem' }}>{si}</span>
          </div>
        </div>

        {/* Creative + platform tiles */}
        <div style={{ padding:'8px 14px', display:'flex', gap:10,
          alignItems:'flex-start', flexWrap:'wrap', borderBottom:'1px solid var(--border)' }}>
          {creative?.image_data && (
            <div style={{ flexShrink:0 }}>
              <img
                src={creative.image_data} alt="creative"
                onClick={() => setLightbox(true)}
                style={{ width:62, height:62, objectFit:'cover', borderRadius:7,
                  border:'2px solid #1db87a', boxShadow:'0 2px 6px rgba(29,184,122,0.22)',
                  cursor:'zoom-in', display:'block' }}
              />
              <div style={{ fontSize:'0.56rem', color:'#0f8050', fontWeight:700,
                marginTop:2, textAlign:'center' }}>✅ Approved</div>
            </div>
          )}
          {/* Creative lightbox */}
          {lightbox && creative?.image_data && (
            <div onClick={() => setLightbox(false)}
              style={{ position:'fixed', inset:0, zIndex:9999,
                background:'rgba(0,0,0,0.82)', display:'flex',
                alignItems:'center', justifyContent:'center' }}>
              <div onClick={e => e.stopPropagation()}
                style={{ position:'relative', maxWidth:'90vw', maxHeight:'90vh' }}>
                <img src={creative.image_data} alt="creative"
                  style={{ maxWidth:'90vw', maxHeight:'85vh', borderRadius:10,
                    boxShadow:'0 8px 40px rgba(0,0,0,0.5)', display:'block' }} />
                <button onClick={() => setLightbox(false)}
                  style={{ position:'absolute', top:-14, right:-14, width:30, height:30,
                    borderRadius:'50%', border:'none', background:'#fff',
                    color:'#222', fontSize:'1rem', fontWeight:900,
                    cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:'0 2px 8px rgba(0,0,0,0.3)' }}>✕</button>
                <div style={{ textAlign:'center', marginTop:8, fontSize:'0.72rem',
                  color:'rgba(255,255,255,0.6)' }}>Click outside or press ESC to close</div>
              </div>
            </div>
          )}
          <div style={{ display:'grid',
            gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:6, flex:1 }}>
            {platformNames.length > 0
              ? platformNames.map(pName => {
                  const pl = findPl(pName);
                  const key = pl ? pl.id : pName;
                  return <PlatformTile key={pName} pName={pName} platRec={platRecs[key]} />;
                })
              : <span style={{ fontSize:'0.75rem', color:'var(--text3)', alignSelf:'center' }}>No platforms assigned</span>
            }
          </div>
        </div>

        {/* Observation history */}
        {logs.length > 0 && (
          <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)' }}>
            {[...logs].reverse().map(log => (
              <LogEntry key={log.id} log={log}
                onEdit={() => setEditLog(log)}
                onDelete={() => deleteLog(log.id)} />
            ))}
          </div>
        )}
        {logs.length === 0 && (
          <div style={{ padding:'8px 14px', textAlign:'center', fontSize:'0.75rem',
            color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>
            No observations yet — add one below.
          </div>
        )}

        {/* Add observation section */}
        <div style={{ padding:'8px 14px', background:'var(--bg3)' }}>
          <div style={{ display:'flex', alignItems:'center',
            justifyContent:'space-between', marginBottom:7 }}>
            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--accent)' }}>
              📝 Add Observations
            </div>
            <button onClick={addRow}
              style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 10px',
                fontSize:'0.7rem', fontWeight:700, background:'var(--bg2)',
                border:'1.5px solid var(--accent)', color:'var(--accent)',
                borderRadius:7, cursor:'pointer' }}>
              ➕ Add Another
            </button>
          </div>
          {obsRows.map((row, i) => (
            <ObsRow key={i} idx={i}
              platforms={platformNames}
              teamMembers={teamMembers}
              data={row}
              onChange={v=>updRow(i,v)}
              onRemove={()=>rmRow(i)}
              platformMetrics={platformMetrics}
            />
          ))}
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
            <button onClick={saveAll} disabled={saving}
              style={{ padding:'6px 18px', fontSize:'0.75rem', fontWeight:700,
                background: saving ? 'var(--bg3)' : 'var(--accent)',
                border:'none', borderRadius:7, cursor: saving ? 'default' : 'pointer',
                color: saving ? 'var(--text3)' : '#fff' }}>
              {saving ? '⏳ Saving…' : '💾 Save All Observations'}
            </button>
          </div>
        </div>
      </div>

      {editLog && (
        <EditObsModal
          log={editLog}
          platforms={platformNames}
          teamMembers={teamMembers}
          platformMetrics={platformMetrics}
          onSave={data => saveEdit(editLog.id, data)}
          onClose={() => setEditLog(null)}
        />
      )}
    </>
  );
}

// ── Closed card (compact read-only) ──────────────────────────────────────────
function ClosedCard({ wo, platRecs, monRec, onReopen }) {
  const logs = monRec.logs || [];
  const [ey,em,ed] = (wo.event_date||'').split('-');
  const evDate = ed ? `${ed}-${em}-${ey}` : wo.event_date;
  const platformNames = Object.values(platRecs)
    .map(rec => rec.platform || rec.name || rec.platform_name)
    .filter(Boolean);

  return (
    <div style={{ background:'var(--bg2)', border:'1px solid var(--border)',
      borderLeft:'4px solid #1db87a', borderRadius:10, marginBottom:10, opacity:0.88 }}>
      <div style={{ padding:'8px 14px', display:'flex', alignItems:'center',
        gap:8, flexWrap:'wrap', borderBottom:'1px solid var(--border)' }}>
        <span style={{ fontFamily:'Space Mono,monospace', fontSize:'0.76rem',
          fontWeight:800, color:'var(--accent)' }}>{wo.wo_number||`WO-${wo.id}`}</span>
        {wo.client?.name && (
          <span style={{ padding:'1px 7px', background:'var(--bg3)',
            borderRadius:9, fontSize:'0.67rem', fontWeight:600 }}>{wo.client.name}</span>
        )}
        <span style={{ fontWeight:700, fontSize:'0.82rem' }}>{wo.title}</span>
        {wo.event_date && (
          <span style={{ fontSize:'0.67rem', color:'var(--text3)' }}>📅 {evDate}</span>
        )}
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:7 }}>
          <span style={{ padding:'2px 9px', background:'#1db87a22', color:'#1db87a',
            borderRadius:9, fontSize:'0.68rem', fontWeight:700 }}>✅ Closed</span>
          <button onClick={onReopen}
            style={{ padding:'3px 9px', fontSize:'0.68rem', fontWeight:700,
              background:'var(--bg3)', border:'1px solid var(--border)',
              borderRadius:6, cursor:'pointer', color:'var(--text2)' }}>
            ↩ Reopen
          </button>
        </div>
      </div>

      {/* Live links row */}
      {platformNames.some(pName => {
        const pl = findPl(pName); const rec = platRecs[pl?pl.id:pName]||{};
        return rec.state==='posted' && (rec.live_url||rec.url||rec.liveUrl);
      }) && (
        <div style={{ padding:'5px 14px 8px', display:'flex', gap:5, flexWrap:'wrap', borderBottom:'1px solid var(--border)' }}>
          {platformNames.map(pName => {
            const pl = findPl(pName);
            const rec = platRecs[pl?pl.id:pName]||{};
            const url = rec.live_url||rec.url||rec.liveUrl||'';
            if (!url || rec.state!=='posted') return null;
            return (
              <a key={pName} href={url} target="_blank" rel="noreferrer"
                style={{ display:'inline-flex', alignItems:'center', gap:4,
                  padding:'3px 9px', background:(pl||{color:'#aaa'}).color,
                  color:'#fff', borderRadius:6, fontSize:'0.68rem',
                  fontWeight:800, textDecoration:'none' }}>
                🔗 {pName}
              </a>
            );
          })}
        </div>
      )}

      <div style={{ padding:'8px 14px' }}>
        {logs.length > 0
          ? [...logs].reverse().map(log => {
              const tc = TAG_COLOR[log.tag]||'#aaa';
              return (
                <div key={log.id} style={{ padding:'6px 9px', background:'var(--bg3)',
                  borderRadius:6, borderLeft:`2px solid ${tc}`, marginBottom:5 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap', marginBottom:2 }}>
                    <span style={{ padding:'1px 6px', background:`${tc}18`, color:tc,
                      borderRadius:7, fontSize:'0.63rem', fontWeight:700 }}>{log.tag}</span>
                    <span style={{ fontSize:'0.68rem', color:'var(--text3)' }}>👤 {log.author||'—'}</span>
                    {log.platform && <span style={{ fontSize:'0.63rem', color:'var(--text3)' }}>{log.platform}</span>}
                    <span style={{ fontSize:'0.62rem', color:'var(--text3)' }}>{fmtDateShort(log.date)}</span>
                    {log.rating && <span style={{ color:'#f7c34f', fontSize:'0.7rem' }}>{stars(log.rating)}</span>}
                    {log.action && log.action!=='None' && (
                      <span style={{ fontSize:'0.63rem', color:'#e84545', fontWeight:700 }}>⚡ {log.action}</span>
                    )}
                  </div>
                  <div style={{ fontSize:'0.76rem', color:'var(--text2)' }}>{log.note}</div>
                  {log.metrics && Object.keys(log.metrics).length>0 && (
                    <div style={{ marginTop:3, display:'flex', gap:8, flexWrap:'wrap' }}>
                      {Object.entries(log.metrics).map(([label,val])=>(
                        <span key={label} style={{ fontSize:'0.63rem', color:'var(--text3)' }}>
                          {label}: <b style={{ color:'var(--text2)' }}>{val}</b>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          : <div style={{ fontSize:'0.7rem', color:'var(--text3)' }}>No observations recorded.</div>
        }
      </div>
    </div>
  );
}

// ── Report Modal ─────────────────────────────────────────────────────────────
function ReportModal({ wos, monData, platRecs, onClose }) {
  const [format, setFormat] = useState('pdf');
  const [inclMetrics, setInclMetrics] = useState(true);
  const [inclObs,     setInclObs]     = useState(true);
  const [inclStatus,  setInclStatus]  = useState(true);
  const [generating,  setGenerating]  = useState(false);
  const reportDate = new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' });

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const buildReportHTML = () => {
    const rows = wos.map(wo => {
      const mon = monData[wo.id] || { status:'Monitoring', logs:[] };
      const logs = mon.logs || [];
      const platforms = Object.values(platRecs[wo.id] || {}).map(p => p.platform || p.name).filter(Boolean);
      return { wo, mon, logs, platforms };
    });

    const statusColor = { 'Monitoring':'#3b7ef0','Needs Attention':'#f7c34f','Action Required':'#e84545','Closed':'#1db87a' };

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Monitoring Report — ${reportDate}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 32px; }
  .cover { text-align: center; padding: 60px 0 40px; border-bottom: 3px solid #6c58f0; margin-bottom: 32px; }
  .cover h1 { font-size: 2rem; font-weight: 900; color: #6c58f0; }
  .cover p { color: #666; margin-top: 8px; }
  .wo-section { border: 1px solid #e2e8f0; border-radius: 10px; margin-bottom: 24px; overflow: hidden; page-break-inside: avoid; }
  .wo-header { padding: 12px 18px; display: flex; align-items: center; gap: 12; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
  .wo-num { font-family: 'Space Mono', monospace; font-weight: 800; color: #6c58f0; font-size: 0.9rem; }
  .status-badge { padding: 3px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 700; color: #fff; margin-left: auto; }
  .wo-body { padding: 14px 18px; }
  .meta { font-size: 0.78rem; color: #666; margin-bottom: 10px; }
  .section-label { font-size: 0.68rem; font-weight: 700; color: #999; text-transform: uppercase; letter-spacing: 0.05em; margin: 12px 0 6px; }
  .platforms { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
  .platform-pill { padding: 3px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 600; background: #ede9fe; color: #6c58f0; }
  .obs-table { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
  .obs-table th { background: #f8fafc; padding: 7px 10px; text-align: left; font-weight: 700; color: #555; font-size: 0.7rem; border-bottom: 2px solid #e2e8f0; }
  .obs-table td { padding: 7px 10px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
  .tag { padding: 2px 7px; border-radius: 10px; font-size: 0.65rem; font-weight: 700; }
  .metrics-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
  .metric-chip { background: #f0faf5; border: 1px solid #d1fae5; border-radius: 6px; padding: 4px 10px; font-size: 0.68rem; }
  .metric-chip span { font-weight: 700; color: #1db87a; }
  .stars { color: #f59e0b; font-size: 0.85rem; }
  @media print { body { padding: 20px; } .wo-section { page-break-inside: avoid; } }
</style>
</head>
<body>
<div class="cover">
  <h1>📡 Monitoring Report</h1>
  <p>Generated on ${reportDate} &nbsp;·&nbsp; ${wos.length} Work Order${wos.length !== 1 ? 's' : ''}</p>
</div>
${rows.map(({ wo, mon, logs, platforms }) => `
<div class="wo-section">
  <div class="wo-header">
    <span class="wo-num">${wo.wo_number || `WO-${wo.id}`}</span>
    <span style="font-weight:700;margin-left:10px;">${wo.title || ''}</span>
    ${wo.client?.name ? `<span style="font-size:0.75rem;color:#666;margin-left:8px;">· ${wo.client.name}</span>` : ''}
    ${inclStatus ? `<span class="status-badge" style="background:${statusColor[mon.status]||'#6c58f0'}">${mon.status}</span>` : ''}
  </div>
  <div class="wo-body">
    ${wo.event_date ? `<div class="meta">📅 Event date: ${wo.event_date}</div>` : ''}
    ${platforms.length > 0 ? `
    <div class="section-label">Platforms</div>
    <div class="platforms">${platforms.map(p => `<span class="platform-pill">${p}</span>`).join('')}</div>
    ` : ''}
    ${inclObs && logs.length > 0 ? `
    <div class="section-label">Observations (${logs.length})</div>
    <table class="obs-table">
      <thead><tr><th>Observer</th><th>Platform</th><th>Tag</th><th>Rating</th><th>Note</th>${inclMetrics ? '<th>Metrics</th>' : ''}</tr></thead>
      <tbody>
      ${logs.map(l => `
        <tr>
          <td>${l.author || '—'}</td>
          <td>${l.platform || '—'}</td>
          <td><span class="tag" style="background:${({'Good':'#d1fae5','Issue':'#fee2e2','Needs Attention':'#fef3c7','Client Feedback':'#ede9fe','Info':'#dbeafe'}[l.tag]||'#f3f4f6')};color:#333">${l.tag}</span></td>
          <td class="stars">${l.rating ? '★'.repeat(l.rating)+'☆'.repeat(5-l.rating) : '—'}</td>
          <td>${l.note || '—'}</td>
          ${inclMetrics ? `<td>${l.metrics && Object.keys(l.metrics).length > 0
            ? Object.entries(l.metrics).map(([k,v]) => v ? `<span class="metric-chip">${k}: <span>${v}</span></span>` : '').filter(Boolean).join(' ')
            : '—'}</td>` : ''}
        </tr>
      `).join('')}
      </tbody>
    </table>
    ` : inclObs ? `<div class="meta" style="color:#999;font-style:italic">No observations recorded.</div>` : ''}
  </div>
</div>
`).join('')}
</body></html>`;
  };

  const generatePDF = () => {
    setGenerating(true);
    const html = buildReportHTML();
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); setGenerating(false); }, 600);
  };

  const generatePPT = () => {
    setGenerating(true);
    // Build a simple HTML slides file that can be printed/saved as presentation reference
    const html = buildReportHTML();
    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `monitoring-report-${new Date().toISOString().slice(0,10)}.html`;
    a.click();
    URL.revokeObjectURL(url);
    setGenerating(false);
  };

  const chkStyle = { display:'flex', alignItems:'center', gap:7, fontSize:'0.8rem', cursor:'pointer' };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9100,
      background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:'var(--bg)', borderRadius:12, width:480, maxWidth:'95vw',
          boxShadow:'0 20px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'18px 22px', borderBottom:'1px solid var(--border)',
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:800, fontSize:'1rem' }}>📊 Generate Report</div>
            <div style={{ fontSize:'0.72rem', color:'var(--text3)', marginTop:2 }}>
              {wos.length} Work Order{wos.length !== 1 ? 's' : ''} selected
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.2rem',
            cursor:'pointer', color:'var(--text3)', padding:'4px 8px' }}>✕</button>
        </div>

        {/* WO list */}
        <div style={{ padding:'14px 22px', borderBottom:'1px solid var(--border)',
          maxHeight:160, overflowY:'auto' }}>
          {wos.map(wo => (
            <div key={wo.id} style={{ display:'flex', alignItems:'center', gap:8,
              padding:'4px 0', borderBottom:'1px solid var(--border)', fontSize:'0.8rem' }}>
              <span style={{ fontFamily:'Space Mono,monospace', fontWeight:700,
                color:'var(--accent)', fontSize:'0.73rem' }}>{wo.wo_number||`WO-${wo.id}`}</span>
              <span style={{ flex:1, color:'var(--text2)' }}>{wo.title}</span>
              {wo.client?.name && <span style={{ fontSize:'0.68rem', color:'var(--text3)' }}>{wo.client.name}</span>}
            </div>
          ))}
        </div>

        {/* Options */}
        <div style={{ padding:'16px 22px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text3)',
            textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Include in Report</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <label style={chkStyle}>
              <input type="checkbox" checked={inclStatus} onChange={e=>setInclStatus(e.target.checked)}
                style={{ accentColor:'#6c58f0' }} />
              Monitoring Status badge
            </label>
            <label style={chkStyle}>
              <input type="checkbox" checked={inclObs} onChange={e=>setInclObs(e.target.checked)}
                style={{ accentColor:'#6c58f0' }} />
              Observation logs
            </label>
            <label style={chkStyle}>
              <input type="checkbox" checked={inclMetrics} onChange={e=>setInclMetrics(e.target.checked)}
                style={{ accentColor:'#6c58f0', opacity: inclObs ? 1 : 0.4 }}
                disabled={!inclObs} />
              <span style={{ opacity: inclObs ? 1 : 0.4 }}>Metrics data</span>
            </label>
          </div>
        </div>

        {/* Format + export */}
        <div style={{ padding:'16px 22px', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:7, overflow:'hidden', flex:1 }}>
            {[['pdf','📄 PDF'],['html','🌐 HTML']].map(([v, label]) => (
              <button key={v} onClick={() => setFormat(v)}
                style={{ flex:1, padding:'8px', fontSize:'0.78rem', fontWeight:700,
                  border:'none', cursor:'pointer',
                  background: format === v ? '#6c58f0' : 'var(--bg2)',
                  color: format === v ? '#fff' : 'var(--text2)' }}>
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={format === 'pdf' ? generatePDF : generatePPT}
            disabled={generating}
            style={{ padding:'9px 20px', fontWeight:800, fontSize:'0.82rem', borderRadius:7,
              border:'none', cursor: generating ? 'wait' : 'pointer',
              background: generating ? '#aaa' : 'linear-gradient(135deg,#6c58f0,#3b7ef0)',
              color:'#fff', boxShadow:'0 2px 8px rgba(108,88,240,0.3)' }}>
            {generating ? '⏳ Generating…' : format === 'pdf' ? '⬇ Export PDF' : '⬇ Download HTML'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Monitoring Page ──────────────────────────────────────────────────────
export default function Monitoring() {
  const { canTab } = useAuth();
  const [tab,          setTab]     = useState('active');
  const [wos,          setWos]     = useState([]);
  const [platRecs,     setPlatRecs]= useState({});
  const [monData,      setMonData] = useState({});
  const [clients,      setClients] = useState([]);
  const [teamMembers,  setTeam]    = useState([]);
  const [loading,      setLoading] = useState(true);
  const [clientF,      setClientF] = useState('');
  const [statusF,      setStatusF] = useState('');
  const [searchQ,      setSearchQ] = useState('');
  const [platF,        setPlatF]   = useState('');
  const [teamF,        setTeamF]   = useState('');
  const [yearF,        setYearF]      = useState('');
  const [monthsF,      setMonthsF]    = useState(new Set()); // empty = all months
  const [showMonthDd,  setShowMonthDd]= useState(false);
  // Multi-select for report
  const [selectedWOs,  setSelectedWOs] = useState(new Set());
  const [showReport,   setShowReport]  = useState(false);
  // Metrics config from Master Data → Report Template
  const [platformMetrics, setPlatformMetrics] = useState(FALLBACK_METRICS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [woRes, clRes, teamRes] = await Promise.all([
        api.get('/scheduling', { params: { posted_only: 1 } }),
        api.get('/clients'),
        api.get('/team'),
      ]);

      // Load platform metrics from master data (Report Template)
      try {
        const mdRes = await api.get('/master-data');
        const md = mdRes.data?.data || mdRes.data || {};
        // Support multiple shapes the backend might use
        const pm = md.platform_metrics || md.report_templates || md.platformMetrics;
        if (pm && typeof pm === 'object') setPlatformMetrics({ ...FALLBACK_METRICS, ...pm });
      } catch { /* use fallback silently */ }

      const rawWos = (woRes.data.data || woRes.data || []).filter(wo => {
        const sps = wo.scheduling_platforms || [];
        return sps.some(sp => sp.state === 'posted');
      });
      setWos(rawWos);
      setClients(clRes.data.data || clRes.data || []);
      setTeam(teamRes.data.data || teamRes.data || []);

      const platMap = {}, monMap = {};
      await Promise.all(rawWos.map(async wo => {
        try {
          const r = await api.get(`/scheduling/${wo.id}`);
          const platforms = r.data.platforms || [];
          const pm = {};
          platforms.forEach(p => {
            const pl = findPl(p.platform || p.name);
            const key = pl ? pl.id : (p.platform || p.name);
            pm[key] = p;
          });
          platMap[wo.id] = pm;

          try {
            const mr = await api.get(`/monitoring/${wo.id}`);
            // show returns { work_order, monitoring: { status, logs } }
            const raw = mr.data?.monitoring ?? mr.data?.data ?? mr.data ?? {};
            // Normalize log field names: backend may use action_required / logged_at
            const normLogs = (raw.logs || []).map(l => ({
              id:        l.id,
              author:    l.author    || '',
              platform:  l.platform  || '',
              tag:       l.tag       || 'Info',
              note:      l.note      || '',
              rating:    l.rating    ?? null,
              action:    l.action    || l.action_required || 'None',
              date:      l.date      || l.logged_at       || l.created_at || null,
              metrics:   l.metrics   || {},
              editedAt:  l.editedAt  || l.edited_at       || null,
            }));
            monMap[wo.id] = { status: raw.status || 'Monitoring', logs: normLogs };
          } catch {
            monMap[wo.id] = { status:'Monitoring', logs:[] };
          }
        } catch { /* skip */ }
      }));

      setPlatRecs(platMap);
      setMonData(monMap);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close month dropdown on outside click
  useEffect(() => {
    if (!showMonthDd) return;
    const h = e => { if (!e.target.closest('[data-month-dd]')) setShowMonthDd(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMonthDd]);

  const reopen = async (woId) => {
    const rec = monData[woId] || { status:'Monitoring', logs:[] };
    try {
      await api.post(`/monitoring/${woId}`, { ...rec, status:'Monitoring' });
      load();
    } catch(e) { console.error(e); }
  };

  // Derive all platforms present in loaded WOs
  const allPlatforms = [...new Set(
    wos.flatMap(wo => Object.values(platRecs[wo.id] || {}).map(p => p.platform || p.name).filter(Boolean))
  )].sort();

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1].map(String);
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const toggleMonth = m => setMonthsF(prev => {
    const s = new Set(prev);
    s.has(m) ? s.delete(m) : s.add(m);
    return s;
  });

  const filtered = wos.filter(wo => {
    if (clientF) {
      const cid = String(wo.client_id || wo.client?.id || '');
      if (cid !== String(clientF)) return false;
    }
    const ms = (monData[wo.id]?.status) || 'Monitoring';
    if (tab === 'active') {
      if (ms === 'Closed') return false;
      if (statusF && ms !== statusF) return false;
    } else {
      if (ms !== 'Closed') return false;
    }
    if (searchQ) {
      const q = searchQ.toLowerCase();
      if (!(wo.wo_number||'').toLowerCase().includes(q) &&
          !(wo.title||'').toLowerCase().includes(q)) return false;
    }
    if (platF) {
      const woPlatforms = Object.values(platRecs[wo.id] || {}).map(p => p.platform || p.name);
      if (!woPlatforms.some(p => (p||'').toLowerCase() === platF.toLowerCase())) return false;
    }
    if (teamF) {
      const logs = monData[wo.id]?.logs || [];
      if (!logs.some(l => l.author === teamF)) return false;
    }
    // Year / Month filter on event_date
    if (wo.event_date) {
      if (yearF && wo.event_date.slice(0, 4) !== yearF) return false;
      if (monthsF.size > 0) {
        const woMonth = parseInt(wo.event_date.split('-')[1], 10);
        if (!monthsF.has(woMonth)) return false;
      }
    }
    return true;
  });

  // Toggle WO selection
  const toggleSelect = id => setSelectedWOs(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });
  const toggleSelectAll = () => {
    if (selectedWOs.size === filtered.length) setSelectedWOs(new Set());
    else setSelectedWOs(new Set(filtered.map(w => w.id)));
  };

  const activeCount = wos.filter(wo=>(monData[wo.id]?.status||'Monitoring')!=='Closed').length;
  const closedCount = wos.filter(wo=>(monData[wo.id]?.status||'Monitoring')==='Closed').length;
  const needsCount  = wos.filter(wo=>{const s=monData[wo.id]?.status;return s==='Needs Attention'||s==='Action Required';}).length;

  const fltStyle = {
    padding: '5px 10px', fontSize: '0.78rem', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--bg2)',
    color: 'var(--text)', cursor: 'pointer',
  };

  return (
    <div>
      {/* ── Sticky header ── */}
      <div style={{
        position: 'sticky', top: '-24px', zIndex: 30,
        background: 'var(--bg)', margin: '-24px -24px 20px',
        padding: '16px 24px 0',
        borderBottom: '1px solid var(--border)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        {/* Tabs row — badges inline on the right, matching Scheduling layout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: '2px solid var(--border)' }}>
          {[
            { key: 'active', label: '📡 Active',  count: activeCount, color: '#3b7ef0' },
            { key: 'closed', label: '✅ Closed',  count: closedCount, color: '#1db87a' },
          ].filter(t => { const cfg = (OPERATIONS_TABS.monitoring||[]).find(x=>x.id===t.key); return !cfg?.permissionKey || canTab(cfg.permissionKey); }).map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setStatusF(''); }}
              style={{ padding: '8px 18px', fontSize: '0.8rem', fontWeight: 700,
                border: 'none', background: 'none', cursor: 'pointer', marginBottom: -2,
                borderBottom: tab === t.key ? `3px solid ${t.color}` : '3px solid transparent',
                color: tab === t.key ? t.color : 'var(--text3)' }}>
              {t.label}
              {t.count > 0 && (
                <span style={{ marginLeft: 5,
                  background: tab === t.key ? `${t.color}22` : 'var(--bg3)',
                  color: tab === t.key ? t.color : 'var(--text3)',
                  borderRadius: 9, padding: '1px 6px', fontSize: '0.67rem', fontWeight: 800 }}>
                  {t.count}
                </span>
              )}

            </button>
          ))}
          {/* Status badges — right side, same row as tabs */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
            {needsCount > 0 && (
              <span style={{ padding: '3px 10px', background: '#e8454518', color: '#e84545',
                borderRadius: 7, fontSize: '0.72rem', fontWeight: 700 }}>
                🔴 {needsCount} need attention
              </span>
            )}
            <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
              {loading ? '⏳' : `${filtered.length} WO${filtered.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>

        {/* Filter row */}
        <div style={{ display: 'flex', gap: 8, padding: '10px 0', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={searchQ} onChange={e => setSearchQ(e.target.value)}
            placeholder="🔍 WO number or title…"
            style={{ ...fltStyle, width: 180, cursor: 'text' }}
          />
          <select value={clientF} onChange={e => setClientF(e.target.value)} style={fltStyle}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {tab === 'active' && (
            <select value={statusF} onChange={e => setStatusF(e.target.value)} style={fltStyle}>
              <option value="">All Status</option>
              <option value="Monitoring">📡 Monitoring</option>
              <option value="Needs Attention">⚠️ Needs Attention</option>
              <option value="Action Required">🔴 Action Required</option>
            </select>
          )}
          <select value={platF} onChange={e => setPlatF(e.target.value)} style={fltStyle}>
            <option value="">All Platforms</option>
            {allPlatforms.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={teamF} onChange={e => setTeamF(e.target.value)} style={fltStyle}>
            <option value="">All Team Members</option>
            {teamMembers.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
          <select value={yearF} onChange={e => setYearF(e.target.value)} style={fltStyle}>
            <option value="">All Years</option>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          {/* Multi-month dropdown */}
          <div data-month-dd style={{ position:'relative' }}>
            <button onClick={() => setShowMonthDd(v => !v)}
              style={{ ...fltStyle, display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
                background: monthsF.size > 0 ? '#6c58f022' : 'var(--bg2)',
                border: `1px solid ${monthsF.size > 0 ? '#6c58f0' : 'var(--border)'}`,
                color: monthsF.size > 0 ? '#6c58f0' : 'var(--text)' }}>
              📅 {monthsF.size === 0 ? 'All Months' : [...monthsF].sort((a,b)=>a-b).map(m=>MONTH_NAMES[m-1]).join(', ')}
              <span style={{ fontSize:'0.6rem', marginLeft:2 }}>▾</span>
            </button>
            {showMonthDd && (
              <div style={{ position:'absolute', top:'110%', left:0, zIndex:200,
                background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8,
                boxShadow:'0 8px 24px rgba(0,0,0,0.15)', padding:10, minWidth:200 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:4 }}>
                  {MONTH_NAMES.map((name, i) => {
                    const m = i + 1;
                    const on = monthsF.has(m);
                    return (
                      <button key={m} onClick={() => toggleMonth(m)}
                        style={{ padding:'5px 8px', fontSize:'0.73rem', fontWeight: on ? 700 : 500,
                          borderRadius:6, border:`1px solid ${on ? '#6c58f0' : 'var(--border)'}`,
                          background: on ? '#6c58f0' : 'var(--bg2)',
                          color: on ? '#fff' : 'var(--text2)', cursor:'pointer' }}>
                        {name}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, gap:6 }}>
                  <button onClick={() => setMonthsF(new Set())}
                    style={{ flex:1, padding:'4px', fontSize:'0.68rem', borderRadius:6, cursor:'pointer',
                      border:'1px solid var(--border)', background:'var(--bg2)', color:'var(--text3)' }}>
                    Clear
                  </button>
                  <button onClick={() => setShowMonthDd(false)}
                    style={{ flex:1, padding:'4px', fontSize:'0.68rem', fontWeight:700, borderRadius:6,
                      cursor:'pointer', border:'none', background:'#6c58f0', color:'#fff' }}>
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {(searchQ || clientF || statusF || platF || teamF || yearF || monthsF.size > 0) && (
            <button onClick={() => { setSearchQ(''); setClientF(''); setStatusF(''); setPlatF(''); setTeamF(''); setYearF(''); setMonthsF(new Set()); }}
              style={{ padding: '5px 10px', fontSize: '0.72rem', borderRadius: 6, cursor: 'pointer',
                border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text3)' }}>
              ✕ Clear
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            {filtered.length > 0 && (
              <button onClick={toggleSelectAll}
                style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 700, borderRadius: 6,
                  cursor: 'pointer', border: '1px solid var(--border)',
                  background: selectedWOs.size === filtered.length ? '#6c58f0' : 'var(--bg2)',
                  color: selectedWOs.size === filtered.length ? '#fff' : 'var(--text2)' }}>
                {selectedWOs.size === filtered.length ? '☑ Deselect All' : '☐ Select All'}
              </button>
            )}
              <button onClick={() => setShowReport(true)}
                disabled={selectedWOs.size === 0}
                style={{ padding: '5px 14px', fontSize: '0.72rem', fontWeight: 700, borderRadius: 6,
                  cursor: selectedWOs.size === 0 ? 'default' : 'pointer', border: 'none',
                  background: selectedWOs.size === 0
                    ? 'var(--bg3)' : 'linear-gradient(135deg,#6c58f0,#3b7ef0)',
                  color: selectedWOs.size === 0 ? 'var(--text3)' : '#fff',
                  boxShadow: selectedWOs.size === 0 ? 'none' : '0 2px 8px rgba(108,88,240,0.3)',
                  transition: 'all 0.2s' }}>
                📊 Generate Report{selectedWOs.size > 0 ? ` (${selectedWOs.size})` : ''}
              </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 20px', color:'var(--text3)' }}>
          <div style={{ fontSize:'2.5rem', marginBottom:10 }}>📡</div>
          <div style={{ fontWeight:600 }}>Loading monitoring data…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px', color:'var(--text3)' }}>
          <div style={{ fontSize:'2.8rem', marginBottom:10, opacity:0.4 }}>
            {tab==='active' ? '📡' : '✅'}
          </div>
          <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--text2)', marginBottom:4 }}>
            {tab==='active' ? 'No active WOs to monitor' : 'No closed WOs yet'}
          </div>
          <div style={{ fontSize:'0.76rem' }}>
            {tab==='active'
              ? 'WOs appear here once a platform is marked Posted in Scheduling & Posting.'
              : 'Mark a WO as Closed on the Active tab to archive it here.'}
          </div>
        </div>
      ) : tab === 'active' ? (
        filtered.map(wo => (
          <div key={wo.id} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
            <input type="checkbox" checked={selectedWOs.has(wo.id)}
              onChange={() => toggleSelect(wo.id)}
              style={{ marginTop:14, width:16, height:16, cursor:'pointer', accentColor:'#6c58f0', flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <MonCard
                wo={wo}
                platRecs={platRecs[wo.id] || {}}
                monRec={monData[wo.id] || { status:'Monitoring', logs:[] }}
                teamMembers={teamMembers}
                onUpdate={load}
                platformMetrics={platformMetrics}
              />
            </div>
          </div>
        ))
      ) : (
        filtered.map(wo => (
          <div key={wo.id} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
            <input type="checkbox" checked={selectedWOs.has(wo.id)}
              onChange={() => toggleSelect(wo.id)}
              style={{ marginTop:12, width:16, height:16, cursor:'pointer', accentColor:'#6c58f0', flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <ClosedCard
                wo={wo}
                platRecs={platRecs[wo.id] || {}}
                monRec={monData[wo.id] || { status:'Closed', logs:[] }}
                onReopen={() => reopen(wo.id)}
              />
            </div>
          </div>
        ))
      )}

      {/* ── Report Modal ── */}
      {showReport && (
        <ReportModal
          wos={filtered.filter(w => selectedWOs.has(w.id))}
          monData={monData}
          platRecs={platRecs}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}